import { ChevronRightIcon } from "lucide-react";
import { type ButtonHTMLAttributes, type FC, type ReactNode, forwardRef } from "react";

import { cn } from "@/agents-v2/utils/cn";

export type RowProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> & {
  /** A small fixed-width lead: a number, a dot, an icon. */
  leading?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  /** Right side: revealed on hover/focus unless `trailingAlways`. */
  trailing?: ReactNode;
  trailingAlways?: boolean;
  /** Show a chevron: the row opens something. */
  chevron?: boolean;
  /** No top border (first row of a group). */
  first?: boolean;
  selected?: boolean;
};

/**
 * One list row: two lines of text, a lead, optional hover-revealed actions.
 * Rows are buttons, so the whole surface is the target and Tab reaches it.
 * (App-level composite on top of shadcn primitives; not a shadcn component.)
 */
export const Row = forwardRef<HTMLButtonElement, RowProps>(
  ({ leading, title, meta, trailing, trailingAlways, chevron, first, selected, className, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "group flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors duration-fast",
        "hover:bg-subtle focus-visible:bg-subtle active:bg-hover focus-visible:outline-none",
        !first && "border-t border-line/70",
        selected && "bg-accent-soft hover:bg-accent-soft",
        className
      )}
      {...rest}
    >
      {leading !== undefined && (
        <span className="flex w-7 shrink-0 items-center text-sm font-semibold text-fg-2">{leading}</span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-fg">{title}</span>
        {meta && <span className="block truncate text-sm text-fg-2">{meta}</span>}
      </span>
      {trailing && (
        <span
          className={cn(
            "flex shrink-0 items-center gap-1",
            !trailingAlways &&
              "opacity-0 transition-opacity duration-fast group-hover:opacity-100 group-focus-visible:opacity-100"
          )}
        >
          {trailing}
        </span>
      )}
      {chevron && <ChevronRightIcon className="size-3.5 shrink-0 text-fg-4" />}
    </button>
  )
);
Row.displayName = "Row";

/** The small label that heads a group of rows. */
export const GroupLabel: FC<{ children: ReactNode; className?: string; trailing?: ReactNode }> = ({
  children,
  className,
  trailing,
}) => (
  <div className={cn("flex items-center px-3 pb-0.5 pt-3 text-xs font-semibold text-fg-2", className)}>
    <span className="flex-1">{children}</span>
    {trailing}
  </div>
);
